import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { User, Bell, Shield, Save, Loader2, Cookie, CreditCard, Trash2, AlertTriangle } from 'lucide-react';
import Portal from './Portal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { getAuthHeaders, user, subscription, refreshSubscription, logout } = useAuth();
  const { openPreferences, consent } = useCookieConsent();
  const [profile, setProfile] = useState({ full_name: '', company_name: '', website: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [notifications, setNotifications] = useState({ email_updates: true, weekly_digest: true, product_news: false });
  const [billingLoading, setBillingLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Only Stripe-direct plans expose billing management / cancel. AppSumo (or other
  // non-stripe_direct) plans must not — cancelling would nuke lifetime access.
  const isStripeDirect = (subscription?.billing_source || 'stripe_direct') === 'stripe_direct';
  const onPaidPlan = subscription && subscription.tier && subscription.tier !== 'free';

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const res = await axios.post(`${API}/payments/portal`, {}, { headers: getAuthHeaders() });
      window.location.href = res.data.portal_url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not open the billing portal.');
      setBillingLoading(false);
    }
  };

  const handleCancelPlan = async () => {
    if (!window.confirm('Cancel your plan and move to Free? You keep any remaining credits.')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { headers: getAuthHeaders() });
      await refreshSubscription();
      alert('Your plan was cancelled. You keep your remaining credits.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not cancel your plan.');
    } finally {
      setCancelling(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/auth/me/export`, { headers: getAuthHeaders() });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stilos-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not export your data.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/me`, {
        headers: getAuthHeaders(),
        data: { confirmation: deleteConfirm, password: deletePassword || undefined },
      });
      logout();
      window.location.href = '/';
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete your account.');
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.full_name || '',
        company_name: user.company_name || '',
        website: user.website || ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me`, profile, { headers: getAuthHeaders() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-in max-w-2xl" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-serif text-3xl mb-1">Settings</h1>
        <p className="text-sm text-[var(--color-muted)]">Manage your account and preferences</p>
      </div>

      {/* Profile */}
      <div className="card p-6 mb-5">
        <div className="flex items-center gap-2.5 mb-5">
          <User className="w-4 h-4 text-[var(--color-coral)]" />
          <h2 className="font-serif text-xl">Profile</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5">Email</label>
            <input type="email" value={user?.email || ''} disabled className="input opacity-60 cursor-not-allowed" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Full Name</label>
            <input type="text" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
              className="input" placeholder="John Doe" data-testid="settings-fullname" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Company Name</label>
            <input type="text" value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
              className="input" placeholder="Acme Inc." data-testid="settings-company" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Website</label>
            <input type="url" value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })}
              className="input" placeholder="https://example.com" data-testid="settings-website" />
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn btn-primary mt-5" data-testid="save-profile-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? (
            <><Save className="w-4 h-4" /> Saved!</>
          ) : (
            <><Save className="w-4 h-4" /> Save changes</>
          )}
        </button>
      </div>

      {/* Notifications */}
      <div className="card p-6 mb-5">
        <div className="flex items-center gap-2.5 mb-5">
          <Bell className="w-4 h-4 text-[var(--color-coral)]" />
          <h2 className="font-serif text-xl">Notifications</h2>
        </div>
        <div className="space-y-4">
          {[
            { key: 'email_updates', label: 'Email Updates', desc: 'Receive updates about your articles and publishing status' },
            { key: 'weekly_digest', label: 'Weekly Digest', desc: 'A summary of your content performance each week' },
            { key: 'product_news', label: 'Product News', desc: 'Announcements about new features and updates' },
          ].map((item) => (
            <div key={item.key} className="flex items-start justify-between p-3 rounded-xl bg-[var(--color-surface)]">
              <div>
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-[10px] text-[var(--color-muted)]">{item.desc}</p>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key] })}
                className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 mt-0.5 ${
                  notifications[item.key] ? 'bg-[var(--color-coral)]' : 'bg-[var(--color-border)]'
                }`}
                data-testid={`toggle-${item.key}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                  notifications[item.key] ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Billing */}
      <div className="card p-6 mb-5">
        <div className="flex items-center gap-2.5 mb-5">
          <CreditCard className="w-4 h-4 text-[var(--color-coral)]" />
          <h2 className="font-serif text-xl">Billing</h2>
        </div>
        <div className="space-y-3">
          {isStripeDirect ? (
            <>
              <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--color-surface)]">
                <div>
                  <p className="text-sm font-medium">Billing &amp; Invoices</p>
                  <p className="text-[10px] text-[var(--color-muted)]">View payment history, download invoices, update payment method</p>
                </div>
                <button onClick={handleManageBilling} disabled={billingLoading} className="btn btn-secondary text-xs" data-testid="manage-billing-btn">
                  {billingLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Manage Billing'}
                </button>
              </div>
              {onPaidPlan && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--color-surface)]">
                  <div>
                    <p className="text-sm font-medium capitalize">{subscription.tier} plan</p>
                    <p className="text-[10px] text-[var(--color-muted)]">Cancel anytime — you keep your remaining credits</p>
                  </div>
                  <button onClick={handleCancelPlan} disabled={cancelling} className="btn btn-secondary text-xs" data-testid="cancel-plan-btn">
                    {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Cancel plan'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="p-3 rounded-xl bg-[var(--color-surface)]">
              <p className="text-sm font-medium">Lifetime access</p>
              <p className="text-[10px] text-[var(--color-muted)]">Your plan isn't billed through Stripe — contact support for any billing questions.</p>
            </div>
          )}
        </div>
      </div>

      {/* Account */}
      <div className="card p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <Shield className="w-4 h-4 text-[var(--color-coral)]" />
          <h2 className="font-serif text-xl">Account</h2>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--color-surface)]">
            <div>
              <p className="text-sm font-medium">Export Data</p>
              <p className="text-[10px] text-[var(--color-muted)]">Download all your articles and data</p>
            </div>
            <button onClick={handleExport} disabled={exporting} className="btn btn-secondary text-xs" data-testid="export-data-btn">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Export'}
            </button>
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-red-50">
            <div>
              <p className="text-sm font-medium text-red-700">Delete Account</p>
              <p className="text-[10px] text-red-600/60">Permanently delete your account and all data</p>
            </div>
            <button onClick={() => setShowDeleteModal(true)} className="btn text-xs bg-red-100 text-red-700 rounded-full" data-testid="delete-account-btn">Delete</button>
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--color-surface)]">
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><Cookie className="w-3.5 h-3.5" /> Cookie Preferences</p>
              <p className="text-[10px] text-[var(--color-muted)]">
                Analytics: {consent.analytics ? 'On' : 'Off'} &middot; Marketing: {consent.marketing ? 'On' : 'Off'} &middot; Preferences: {consent.preferences ? 'On' : 'Off'}
              </p>
            </div>
            <button onClick={openPreferences} className="btn btn-secondary text-xs" data-testid="settings-cookie-preferences-btn">Manage</button>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <Portal>
        <div className="fixed inset-0 bg-black/30 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-[20px] w-full max-w-md my-8 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="delete-account-modal">
            <div className="p-6 border-b border-[var(--color-border)] flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-red-50">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="font-serif text-lg">Delete account</h2>
                <p className="text-[10px] text-[var(--color-muted)]">This is permanent and cannot be undone</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-xs text-[var(--color-muted)]">
                This permanently deletes your account and all your content — articles, brand voices, integrations, and remaining credits. Records of any payments are kept in a form not linked to your Stilos profile for legal/accounting reasons. This cannot be undone.
              </p>
              <div>
                <label className="block text-xs font-medium mb-1.5">Type <span className="font-mono font-semibold">DELETE</span> to confirm</label>
                <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} className="input" placeholder="DELETE" data-testid="delete-confirm-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5">Your password</label>
                <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} className="input" placeholder="Enter your password" data-testid="delete-password-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); setDeletePassword(''); }} className="btn btn-secondary flex-1">Cancel</button>
                <button onClick={handleDeleteAccount} disabled={deleting || deleteConfirm !== 'DELETE'} className="btn flex-1 bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete my account'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
};

export default Settings;
